import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertTriangle, ShieldCheck } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { mfaApi } from '../../services/api/mfa'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Label } from '../ui/Label'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from '../ui/Dialog'
import { DialogHeaderWithIcon } from '../ui/DialogHeaderWithIcon'
import toast from 'react-hot-toast'

interface MfaUnenrollDialogProps {
  open: boolean
  factorId: string
  onClose: () => void
  onUnenrolled: () => void
}

const unenrollSchema = z.object({
  code: z
    .string()
    .min(6, 'Enter the 6-digit code')
    .max(6, 'Code must be exactly 6 digits')
    .regex(/^\d{6}$/, 'Code must be 6 digits'),
})

type UnenrollFormData = z.infer<typeof unenrollSchema>

export const MfaUnenrollDialog: React.FC<MfaUnenrollDialogProps> = ({
  open,
  factorId,
  onClose,
  onUnenrolled,
}) => {
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset: resetForm,
  } = useForm<UnenrollFormData>({
    resolver: zodResolver(unenrollSchema),
  })

  const onSubmit = async (data: UnenrollFormData) => {
    setSubmitting(true)
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      })
      if (challengeError) throw challengeError

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: data.code,
      })

      if (verifyError) {
        setError('code', { message: 'Invalid code. Please try again.' })
        return
      }

      const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId })
      if (unenrollError) throw unenrollError

      await mfaApi.recordAudit('mfa.unenrolled', factorId)
      toast.success('Authenticator app removed.')
      onUnenrolled()
      onClose()
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove authenticator app')
    } finally {
      setSubmitting(false)
      resetForm()
    }
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogHeaderWithIcon
            icon={ShieldCheck}
            iconColor="rose"
            title="Remove authenticator app"
            description="Verify your identity to disable two-factor authentication."
          />
        </DialogHeader>
        <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800/40">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-300">
            Removing your authenticator app will disable two-factor authentication on your account.
          </p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="unenroll-code">
              Enter your current authentication code to confirm
            </Label>
            <Input
              {...register('code')}
              id="unenroll-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
              className="text-center text-2xl tracking-[0.5em] font-mono"
              autoFocus
            />
            {errors.code && (
              <p className="text-sm text-destructive">{errors.code.message}</p>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              className="flex-1"
              disabled={submitting}
            >
              {submitting ? (
                <LoadingSpinner size="sm" variant="icon" className="mr-2" />
              ) : null}
              Remove
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
